import * as React from "react";
import { useCallback } from "react";
import type { FC, PropsWithChildren, ReactNode } from "react";
import areEqual from "fbjs/lib/areEqual";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";

import { IterUtils } from "../../../../../Common/IterableUtils";
import { useUUID } from "../../../../../Common/UUID";
import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    createRelativeFromTokens,
    emptyModelPath,
    ModelPath,
    PathTokens,
    ResolvedModelPath,
} from "../../../../../Common/ModelPath/ModelPath";
import { arrayIsNotEmpty, isNotNullOrUndefined, noop } from "../../../../../Common/TypingUtils";
import { ModelHunk } from "../../../Engine/Core/ModelStoring/ModelHunk";
import { smoothScrollBy } from "../../../Engine/utils";
import { RemoveRowButton } from "../../../Forms/InjectableModules/InjectableControls";
import {
    ChangeRowHeightHandler,
    MultilineRowHeightConsumer,
    MultilineRowHeightProvider,
    RowRemovedHandler,
} from "../../Internals/ControlContexts/MultilineRowHeightContext";
import { ProvideInstanceIndex } from "../../Internals/ControlContexts/PathContexts/InstanceIndexContext";
import { PushInstance } from "../../Internals/ControlContexts/PathContexts/PushInstance";
import { LayoutEvents } from "../../Internals/LayoutEvents/LayoutEvents";
import { MetricsActionPayload, trackAction } from "../../Internals/Metrics/MetricsProvider";
import { MULTILINE_UNIT_CLICK } from "../../metricsAction";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { TestPageObjectHandle } from "../../Services/TestPageObjects/TestPageObjectHandle";
import { emptyRef, isChildHtmlElement, isChildOfType, nullElement, pullContext } from "../../TypingUtils";
import { AddRowButtonContainer as AddRowButton } from "../AddRowButton/AddRowButtonContainer";
import { StickyTableBodyRowContainer } from "../StickyTable/Body/StickyTableBodyRowContainer";

import { ExcelPasteUtils } from "./ExcelPasteUtils";
import { EmptyRow } from "./Internals/EmptyRow";
import { Row } from "./Internals/Row";
import { RowAdderCell } from "./MultiLine.styles";
import { MultiLineFilters } from "./MultiLineFilter";
import { DataPasteContext, DatePasteStatus, MultilineDataPasteContext } from "./TableDataPasteContext";
import { MultiLineLoader } from "./MultiLineLoader/MultiLineLoader";
import { SimpleRow } from "./Internals/SimpleRow";

function multilineUnitMetricParams(props: MultiLineProps): MetricsActionPayload {
    return { onRowClick: isNotNullOrUndefined(props.onRowClick) };
}

export interface MultiLineProps {
    // addInstance тут только для adapter-а
    addInstance: (filteredUniqKey: string) => void;
    onRemoveInstance: (instanceToRemove: string) => void;
    filteredUniqKey: string;
    addButton?: boolean | string;
    bindingPath: AbsoluteResolvedModelPath;
    children: React.ReactNode;
    className?: string;
    currentPage: number;
    disabled?: boolean;
    disableScroll?: boolean;
    goToPage: (pageNumber: number) => void;
    displayedInstances: readonly string[];
    instancesPerPage: number;
    isList?: boolean;
    removeButton?: boolean;
    removeButtonPosition?: "first";
    scrollOnChange?: boolean;
    filters?: MultiLineFilters;
    sorted: boolean;
    showRowBorder?: boolean;
    loading?: boolean;
    columnsStructure?: AbsoluteModelPath[];
    fillMultiline: (values: ModelHunk, newInstanceCount: number, filteredUniqKey: string) => void;
    fillInternalMultiField: (
        pathToInternalMultiField: ResolvedModelPath,
        values: ModelHunk,
        newInstanceCount: number,
        filteredUniqKey: string
    ) => void;
    getInternalMultiFiledChildren: (pathToInternalMultiField: ResolvedModelPath) => readonly string[];
    onRowClick?: (instance: string) => void;
    highlightHoveredRow?: boolean;
    isReadOnly?: boolean;
    dynamicHeight: boolean;
}

interface State {
    rowHeight: number;
}

interface MultiLineContext {
    getFooterHeight: () => number;
    getHeaderHeight: () => number;
    recalcHeightRows?: () => void;
}

export class MultiLine extends React.Component<MultiLineProps, State> {
    public state: State = { rowHeight: 0 };
    private lastRowDom?: null | Element;
    private scrollEnabled?: boolean | void;
    private firstRowDom?: null | Element;
    private recentAddedRowDom?: null | Element;
    private readonly filteredUniqKey: string;

    public static defaultProps = {
        instancesPerPage: 10,
        currentPage: 1,
        scrollOnChange: true,
        disabled: false,
    };

    public static contextTypes = {
        registerMultilinePasteFunc: PropTypes.func,
        getHeaderHeight: PropTypes.func,
        getFooterHeight: PropTypes.func,
        recalcHeightRows: PropTypes.func,
    };

    public constructor(props: MultiLineProps, context: unknown) {
        super(props, context);
        this.filteredUniqKey = this.props.filteredUniqKey ?? "default";
    }

    public componentDidMount(): void {
        const pastRegFunc = pullContext<MultilineDataPasteContext>(this).registerMultilinePasteFunc;
        this.setState({ rowHeight: this.getRowHeight() });
        if (pastRegFunc != undefined) {
            pastRegFunc((excelData, { bindingPath }, columnStartIndex) =>
                this.pasteTableData(excelData, bindingPath, this.props.displayedInstances[0] ?? "0", columnStartIndex)
            );
        }
    }

    public shouldComponentUpdate(nextProps: MultiLineProps, nextState: State): boolean {
        return (
            !nextProps.bindingPath.isEquals(this.props.bindingPath) ||
            nextProps.currentPage !== this.props.currentPage ||
            nextProps.displayedInstances.length !== this.props.displayedInstances.length ||
            nextProps.disabled !== this.props.disabled ||
            nextState.rowHeight !== this.state.rowHeight ||
            nextProps.loading !== this.props.loading ||
            !areEqual(nextProps.filters, this.props.filters) ||
            !areEqual(nextProps.displayedInstances, this.props.displayedInstances)
        );
    }

    public componentDidUpdate(prevProps: MultiLineProps): void {
        const isInstanceCountChanged = this.props.displayedInstances.length !== prevProps.displayedInstances.length;
        const isFilteredChanged =
            !areEqual(prevProps.filters, this.props.filters) || this.props.sorted !== prevProps.sorted;
        const isMultilineCleared = this.props.displayedInstances.length <= 1 && this.props.currentPage != 1;

        if (isInstanceCountChanged && !isFilteredChanged) {
            LayoutEvents.emit();
        }

        const rowHeight = this.state.rowHeight || this.getRowHeight();

        if (!this.state.rowHeight && !this.props.isList) {
            this.setState({ rowHeight: rowHeight });
        }

        if (!this.props.isList && isMultilineCleared) {
            //принудительный сброс номера текущей страницы при очистке мультилайна из действий с разделом
            this.props.goToPage(1);
        }

        const isChangePage = this.props.currentPage !== prevProps.currentPage;
        const lastPage = Math.ceil(prevProps.displayedInstances.length / this.props.instancesPerPage) || 1;

        if (
            this.scrollEnabled &&
            this.props.scrollOnChange &&
            lastPage &&
            isInstanceCountChanged &&
            !isFilteredChanged
        ) {
            const isRemovedInstance = this.props.displayedInstances.length < prevProps.displayedInstances.length;
            const isAddInstance = this.props.displayedInstances.length > prevProps.displayedInstances.length;
            const isLastInstanceChanged =
                this.props.displayedInstances.slice(-1)[0] !== prevProps.displayedInstances.slice(-1)[0];

            this.scroll(isRemovedInstance, isAddInstance, isChangePage, isLastInstanceChanged);
        } else {
            LayoutEvents.emit();
        }
        this.scrollEnabled = !this.props.disableScroll;
    }

    public render(): JSX.Element {
        const instances = this.getInstances();
        return (
            <TestPageObjectHandle
                id={`${this.props.bindingPath.toLegacyPath()}/${this.props.filteredUniqKey}`}
                pageObject={{
                    getType: () => "MultiLine",
                    getPageCount: () => this.getPageCount(),
                    getPage: () => this.props.currentPage,
                    setPage: (value: number) => this.props.goToPage(value),
                    add: () => this.props.addInstance(this.filteredUniqKey),
                    getRowCount: () => this.props.displayedInstances.length,
                    getValue: () => `${this.getInstances()}`,
                    getInstances: () => this.props.displayedInstances,
                    deleteInstance: (instanceToRemove: string) => this.props.onRemoveInstance(instanceToRemove),
                }}>
                <FocusManagementObserver
                    onRequestFocus={noop}
                    onRequestPathAvailability={this.handleRequestPathAvailability}>
                    <MultilineRowHeightProvider bindingPath={this.props.bindingPath}>
                        {({ onChangeLastRowHeight }) => (
                            <tbody>
                                {this.renderRowsOrSpinner(instances)}
                                {this.props.dynamicHeight ? null : (
                                    <EmptyRow
                                        height={this.state.rowHeight * (this.props.instancesPerPage - instances.length)}
                                        isView={
                                            !this.props.isList &&
                                            this.props.instancesPerPage - instances.length > 0 &&
                                            this.props.displayedInstances.length > this.props.instancesPerPage
                                        }
                                        onHeightChange={onChangeLastRowHeight}
                                    />
                                )}
                                {!this.props.isReadOnly && this.props.addButton !== false && (
                                    <SimpleRow>
                                        <RowAdderCell>
                                            <AddRowButton
                                                bindingPath={this.props.bindingPath}
                                                disabled={this.props.disabled}
                                                text={this.props.addButton}
                                                filteredUniqKey={this.props.filteredUniqKey}
                                            />
                                        </RowAdderCell>
                                    </SimpleRow>
                                )}
                            </tbody>
                        )}
                    </MultilineRowHeightProvider>
                </FocusManagementObserver>
            </TestPageObjectHandle>
        );
    }

    public readonly goToPageByInstanceIdx = (instance: number): void => {
        const instanceIdx = this.props.displayedInstances.findIndex(inst => Number(inst) === instance) + 1;
        this.props.goToPage(
            Math.ceil(Math.min(instanceIdx, this.props.displayedInstances.length) / this.props.instancesPerPage)
        );
    };

    public getPageCount(): number {
        return Math.ceil(this.props.displayedInstances.length / this.props.instancesPerPage) || 1;
    }

    public scroll(
        isRemovedInstance?: boolean,
        isAddInstance?: boolean,
        isChangePage?: boolean,
        isLastInstanceChanged?: boolean
    ): void {
        if (
            !this.props.displayedInstances.length ||
            !this.getContext().getHeaderHeight ||
            !this.getContext().getFooterHeight
        ) {
            return;
        }

        if (isRemovedInstance) {
            if (isChangePage || isLastInstanceChanged) {
                this.scrollToBottom();
            }
        }

        if (isAddInstance) {
            if (isChangePage) {
                if (isLastInstanceChanged) {
                    this.scrollToBottom();
                } else {
                    this.scrollToFirst();
                }
            } else if (isLastInstanceChanged) {
                this.scrollToBottom();
            } else {
                this.scrollToBottom(this.recentAddedRowDom);
            }
        }
    }

    public getInstances(): readonly string[] {
        return this.props.isList
            ? this.props.displayedInstances
            : this.props.displayedInstances.slice(
                  (this.props.currentPage - 1) * this.props.instancesPerPage,
                  this.props.currentPage * this.props.instancesPerPage
              );
    }

    private readonly processTableDataPaste = (
        clipboardContent: DataTransfer,
        bindingPath: AbsoluteModelPath,
        rowStartIndex: string,
        columnStartIndex?: number
    ): DatePasteStatus => {
        const excelData = ExcelPasteUtils.parseClipboardContent(clipboardContent);

        return this.pasteTableData(excelData, bindingPath, rowStartIndex, columnStartIndex);
    };

    private readonly pasteTablesToInternalMultiField = (
        additionalExcelData: Map<number, string[][]>,
        columnStartIndex: number,
        columnNames: ModelPath[],
        rowStartIndex: number
    ): void => {
        const [columnsPrefixes, multiFieldColumns] = ExcelPasteUtils.splitColumnNames(columnNames);

        additionalExcelData.entries().forEach(([multilineRowIndex, excelData]) => {
            const additionalResult = ExcelPasteUtils.pasteTableData(
                excelData,
                [],
                multiFieldColumns,
                1,
                columnStartIndex,
                true,
                true
            );

            if (additionalResult !== DatePasteStatus.Failure) {
                const pathsToMultiField = ExcelPasteUtils.getPathsToMultiFieldByColumn(
                    additionalResult.data,
                    columnsPrefixes,
                    multiFieldColumns,
                    multilineRowIndex,
                    rowStartIndex,
                    this.props.displayedInstances
                );

                const pathToMultiField = arrayIsNotEmpty(pathsToMultiField)
                    ? IterUtils.first(pathsToMultiField)
                    : emptyModelPath();

                const currentMultiFieldChildren = this.props.getInternalMultiFiledChildren(pathToMultiField).length;
                const newInstanceCount =
                    currentMultiFieldChildren <= additionalResult.newInstanceCount
                        ? additionalResult.newInstanceCount - currentMultiFieldChildren
                        : 0;

                this.props.fillInternalMultiField(
                    pathToMultiField,
                    additionalResult.data,
                    newInstanceCount,
                    this.filteredUniqKey
                );
            }
        });
    };

    private readonly pasteTableWithInternalMultiFieldTablesData = (
        excelData: string[][],
        columnNames: ModelPath[],
        columnStartIndex: number,
        rowStartIndex: number
    ): DatePasteStatus => {
        const { mainExcelData, additionalExcelData } = ExcelPasteUtils.splitExcelData(
            excelData,
            columnNames,
            columnStartIndex
        );
        const simpleColumnNames = columnNames.map(mp => mp.resolveAll("0"));

        const result = ExcelPasteUtils.pasteTableData(
            mainExcelData,
            this.props.displayedInstances,
            simpleColumnNames,
            rowStartIndex,
            columnStartIndex
        );

        if (result === DatePasteStatus.Failure) {
            return DatePasteStatus.Failure;
        } else {
            this.props.fillMultiline(result.data, result.newInstanceCount, this.filteredUniqKey);
            if (this.getInstances().length === 0) {
                this.props.goToPage(1);
            }
        }

        this.pasteTablesToInternalMultiField(additionalExcelData, columnStartIndex, columnNames, rowStartIndex);

        return DatePasteStatus.SuccessAll;
    };

    private readonly pasteTableData = (
        excelData: string[][],
        bindingPath: AbsoluteModelPath,
        rowStartName: string,
        columnStartIndex?: number
    ): DatePasteStatus => {
        if (this.props.columnsStructure == undefined) {
            return DatePasteStatus.Failure;
        }
        columnStartIndex =
            columnStartIndex ?? ExcelPasteUtils.findColumnStartIndex(bindingPath, this.props.columnsStructure);

        const rowStartIndex = ExcelPasteUtils.findRowStartIndex(rowStartName, this.props.displayedInstances);

        const columnNamesResult = this.getColumnNames();
        return this.pasteTableWithInternalMultiFieldTablesData(
            excelData,
            columnNamesResult.columns,
            columnStartIndex,
            rowStartIndex
        );
    };

    private readonly getColumnNames = (): { columns: ModelPath[]; internalMultiField: boolean } => {
        if (this.props.columnsStructure == undefined || this.props.columnsStructure.length == 0) {
            return { internalMultiField: false, columns: [] };
        }

        const columnsParts = this.props.columnsStructure.map(bp => bp.getPathPartsAsArray());
        const pathsLength = columnsParts.map(parts => parts.length);
        const someColumnPathPartsLength = IterUtils.first(pathsLength);
        const columnsStructureHasDifferentLengths = pathsLength.some(l => l !== someColumnPathPartsLength);
        if (columnsStructureHasDifferentLengths) {
            const minLength = Math.min(...pathsLength);
            if (columnsParts.some(p => p.slice(minLength - 1).some(token => PathTokens.isMultiToken(token)))) {
                return {
                    columns: columnsParts.map(parts =>
                        arrayIsNotEmpty(parts) ? createRelativeFromTokens(parts.slice(minLength - 1)) : emptyModelPath()
                    ),
                    internalMultiField: true,
                };
            }
            return {
                columns: columnsParts.map(parts =>
                    arrayIsNotEmpty(parts)
                        ? createRelativeFromTokens(
                              parts.slice(minLength - 1).map(token => (PathTokens.isMultiToken(token) ? "0" : token))
                          )
                        : emptyModelPath()
                ),
                internalMultiField: false,
            };
        }
        return {
            columns: columnsParts.map(parts =>
                arrayIsNotEmpty(parts) ? createRelativeFromTokens([IterUtils.last(parts)]) : emptyModelPath()
            ),
            internalMultiField: false,
        };
    };

    private getContext(): MultiLineContext {
        return pullContext<MultiLineContext>(this);
    }

    private getRowHeight(): number {
        if (!this.firstRowDom) {
            return 0;
        }

        const { top } = this.firstRowDom.getBoundingClientRect();

        if (!this.lastRowDom) {
            return 0;
        }
        const { bottom } = this.lastRowDom.getBoundingClientRect();

        return (bottom - top) / this.getInstances().length;
    }

    private scrollToBottom(row: undefined | null | Element = this.lastRowDom): void {
        const firstRow = this.firstRowDom;
        const lastRow = this.lastRowDom;

        if (!row || !firstRow || !lastRow || this.isVisibleRow(row)) {
            return;
        }

        const { bottom } = row.getBoundingClientRect();
        const { bottom: lastRowBottom } = lastRow.getBoundingClientRect();
        const { top } = firstRow.getBoundingClientRect();

        const headerHeight = this.getContext().getHeaderHeight();
        const footerHeight = this.getContext().getFooterHeight();
        const screenHeight = document.documentElement ? document.documentElement.offsetHeight : 0;
        const isMoreVisibleScreen = screenHeight - headerHeight - footerHeight > lastRowBottom - top;

        if (isMoreVisibleScreen && top < headerHeight) {
            this.scrollToFirst();

            return;
        }

        smoothScrollBy(bottom - screenHeight + footerHeight + 10);
    }

    private scrollToFirst(): void {
        const firstRow = this.firstRowDom;

        if (!firstRow || this.isVisibleRow(firstRow)) {
            return;
        }

        const { top } = firstRow.getBoundingClientRect();
        const headerHeight = this.getContext().getHeaderHeight();

        smoothScrollBy(top - headerHeight + 10);
    }

    private isVisibleRow(row: Element): boolean {
        const headerHeight = this.getContext().getHeaderHeight();
        const footerHeight = this.getContext().getFooterHeight();
        const screenHeight = document.documentElement ? document.documentElement.offsetHeight : 0;

        const { top, bottom } = row.getBoundingClientRect();

        return top > headerHeight && bottom < screenHeight - footerHeight;
    }

    private readonly _refFirstRow = (row: null | Row): void => {
        if (row != undefined) {
            const firstRowDom = ReactDOM.findDOMNode(row);
            if (firstRowDom instanceof Element) {
                this.firstRowDom = row ? firstRowDom : emptyRef;
            }
        }

        if (this.getInstances().length === 1) {
            this._refLastRow(row);
        }
    };

    private readonly _refCurrentRow = (row: null | Row): void => {
        if (row != undefined) {
            const recentAddedRowDom = ReactDOM.findDOMNode(row);
            if (recentAddedRowDom instanceof Element) {
                this.recentAddedRowDom = recentAddedRowDom;
            }
        }
    };

    private readonly _refLastRow = (row: null | Row): void => {
        this._refCurrentRow(row);
        if (row) {
            const lastRowDom = ReactDOM.findDOMNode(row);
            if (lastRowDom instanceof Element) {
                this.lastRowDom = lastRowDom;
            }
            // Need for scroll, because last row is lost. ref not update for rendered element
        } else {
            this.lastRowDom = this.firstRowDom;
        }
    };

    private renderRowsOrSpinner(instances: readonly string[]): JSX.Element[] | JSX.Element {
        return (
            <>
                {(this.props.loading ?? false) ? (
                    <tr>
                        <td>
                            <MultiLineLoader active={this.props.loading ?? false}>
                                <></>
                            </MultiLineLoader>
                        </td>
                    </tr>
                ) : (
                    <></>
                )}

                {this.renderRows(instances)}
            </>
        );
    }

    private renderRows(instances: readonly string[]): JSX.Element[] {
        const multilineRows = instances.map((instanceId: string, instanceIndex: number) => {
            const removeButton =
                this.props.removeButton === false || this.props.isReadOnly ? (
                    nullElement
                ) : (
                    <td>
                        <PushInstance instance={instanceId} bindingPath={this.props.bindingPath}>
                            <RemoveRowButton
                                disabled={this.props.disabled}
                                filteredUniqKey={this.props.filteredUniqKey}
                            />
                        </PushInstance>
                    </td>
                );

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const rows: any[] = React.Children.toArray(this.props.children).filter(
                x => isChildHtmlElement(x, "tr") || isChildOfType(x, StickyTableBodyRowContainer)
            );

            if (rows.length === 0) {
                rows.push(this);
            }

            return rows.map((row: { props: { children?: React.ReactNode; bindingPath: string } }, rowIndex: number) => {
                const isFirstRow = instanceIndex === 0 && rowIndex === 0;
                const isLastRow = instanceIndex === instances.length - 1 && rowIndex === rows.length - 1;

                let rowCallbackRef;
                if (isFirstRow) {
                    rowCallbackRef = this._refFirstRow;
                } else if (isLastRow) {
                    rowCallbackRef = this._refLastRow;
                } else {
                    rowCallbackRef = this._refCurrentRow;
                }

                return (
                    <DataPasteContext.Provider
                        key={`multiline_row_${instanceId}_${rowIndex}`}
                        value={(dataTransfer, { bindingPath }) =>
                            this.processTableDataPaste(dataTransfer, bindingPath, instanceId)
                        }>
                        <ProvideInstanceIndex
                            index={
                                this.props.isList
                                    ? instanceIndex
                                    : instanceIndex + (this.props.currentPage - 1) * this.props.instancesPerPage
                            }
                            bindingPath={this.props.bindingPath}>
                            <PushInstance instance={instanceId} bindingPath={this.props.bindingPath}>
                                <MultilineRowHeightProvider bindingPath={this.props.bindingPath}>
                                    {({ onHeightChanged, onRowRemoved }) => (
                                        <DynamicHeightRow
                                            key={`multiline_row_${instanceId}_${rowIndex}`}
                                            showRowBorder={this.props.showRowBorder ?? false}
                                            bindingPath={this.props.bindingPath}
                                            instanceId={instanceId}
                                            instanceIndex={instanceIndex}
                                            rowIndex={rowIndex}
                                            removeButtonPosition={this.props.removeButtonPosition}
                                            highlightHoveredRow={this.props.highlightHoveredRow ?? false}
                                            removeButton={rowIndex === 0 ? removeButton : <td />}
                                            onClick={this.handleOnClick}
                                            callbackRef={rowCallbackRef}
                                            onHeightChanged={onHeightChanged}
                                            onRowRemoved={onRowRemoved}>
                                            {row.props.children}
                                        </DynamicHeightRow>
                                    )}
                                </MultilineRowHeightProvider>
                            </PushInstance>
                        </ProvideInstanceIndex>
                    </DataPasteContext.Provider>
                );
            });
        });

        return multilineRows.flat();
    }

    @trackAction(MULTILINE_UNIT_CLICK, multilineUnitMetricParams)
    private readonly handleOnClick = (instance: string) => {
        if (this.props.onRowClick != undefined) {
            this.props.onRowClick(instance);
        }
    };

    private readonly handleRequestPathAvailability = async (path: AbsoluteModelPath) => {
        const lastToken = IterUtils.last(path.getPathParts());
        if (PathTokens.isInstanceToken(lastToken)) {
            this.goToPageByInstanceIdx(Number(lastToken));
        }
    };
}

type RowProps = PropsWithChildren<{
    showRowBorder: boolean;
    bindingPath: AbsoluteResolvedModelPath;
    instanceId: string;
    instanceIndex: number;
    rowIndex: number;
    removeButtonPosition?: "first";
    highlightHoveredRow: boolean;
    removeButton: ReactNode;
    onClick: (instance: string) => void;
    callbackRef: (row: Row | null) => void;
    onHeightChanged: ChangeRowHeightHandler;
    onRowRemoved: RowRemovedHandler;
}>;

const DynamicHeightRow: FC<RowProps> = props => {
    const id = useUUID();

    const onHeightChanged = useCallback(
        (rowHeight: number) => {
            props.onHeightChanged({
                instanceId: props.instanceId,
                row: {
                    id: id,
                    index: props.rowIndex.toString(),
                    height: rowHeight,
                },
            });
        },
        [props.onHeightChanged, props.instanceId, props.rowIndex]
    );

    return (
        <MultilineRowHeightConsumer bindingPath={props.bindingPath}>
            {({ rowHeight }) => (
                <Row
                    onRowRemoved={props.onRowRemoved}
                    showRowBorder={props.showRowBorder}
                    ref={props.callbackRef}
                    removeButton={props.removeButton}
                    removeButtonPosition={props.removeButtonPosition}
                    highlightHoveredRow={props.highlightHoveredRow}
                    height={rowHeight[props.instanceId]?.rowHeights[props.rowIndex]?.maxHeight ?? 0}
                    instanceId={props.instanceId}
                    onClick={props.onClick}
                    onHeightChange={onHeightChanged}>
                    {props.children}
                </Row>
            )}
        </MultilineRowHeightConsumer>
    );
};
